import { useCallback, useEffect, useState } from "react";

import toast from "react-hot-toast";

import { BuildingIcon, InfoIcon, XIcon } from "lucide-react";

import { Button } from "simplydo/react/ui/button";
import { Card, CardContent } from "simplydo/react/ui/card";
import { H3 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Message, MessageHeader } from "simplydo/react/ui/message";

import api from "api";
import { useAppSelector } from "store";

import CompanyVerificationReportGenerator from "components/lib/CompanyVerificationReportGenerator";
import { IdeaCompanyVerification } from "components/lib/Ideas";
import type {
  IdeaCompanyVerificationReport,
  IdeaWithOwnerChallenge,
} from "components/lib/Ideas/types";

import { CompanyReportFieldValue, FieldProps } from "./FieldProps";

const CompanyReport = ({
  field,
  ideaId,
  challengeId,
  fieldValue,
  updateIdea,
  isEditing,
}: FieldProps) => {
  const user = useAppSelector((state) => state.user);
  const reportValue = (fieldValue as CompanyReportFieldValue) || null;
  const [creditsafeEnabled, setCreditsafeEnabled] = useState(false);
  const [
    hasHandledShowingCreditsafeLookup,
    setHasHandledShowingCreditsafeLookup,
  ] = useState(false);

  useEffect(() => {
    if (!hasHandledShowingCreditsafeLookup) {
      setHasHandledShowingCreditsafeLookup(true);
      api.challenges.get(
        challengeId,
        (challenge) => {
          setCreditsafeEnabled(!!challenge.creditsafeAvailable);
        },
        () => {
          setCreditsafeEnabled(false);
        },
      );
    }
  }, [challengeId, hasHandledShowingCreditsafeLookup]);

  const updateReportUrl = useCallback(
    (reportUrl: string) => {
      if (!reportValue) {
        return;
      }
      updateIdea({ ...reportValue, reportUrl }, ["templated", field.id], true);
    },
    [updateIdea, reportValue, field.id],
  );

  if (!isEditing) {
    if (reportValue) {
      return (
        <IdeaCompanyVerification
          idea={{ _id: ideaId } as IdeaWithOwnerChallenge}
          report={reportValue as IdeaCompanyVerificationReport}
          updateReportUrl={updateReportUrl}
          field={field}
        />
      );
    }
    return null;
  }
  if (!reportValue && !creditsafeEnabled) {
    return (
      <Message color="blue">
        <Icon icon={InfoIcon} />
        <MessageHeader>
          We have detected an issue with the company lookup service. Please
          enter your information manually or contact support for assistance.
        </MessageHeader>
      </Message>
    );
  }

  return (
    <Card fluid placeholder>
      <CardContent>
        {!reportValue ? (
          <>
            <H3>
              <Icon icon={BuildingIcon} /> Company lookup
            </H3>
            <CompanyVerificationReportGenerator
              user={user!}
              trigger={<Button color="primary">Find your company</Button>}
              forType="idea"
              forId={ideaId}
              onComplete={(
                report: CompanyReportFieldValue & { tempReportUrl?: string },
              ) => {
                const { tempReportUrl } = report;
                delete report.tempReportUrl;
                updateIdea({ report: report._id, ...report }, [
                  "templated",
                  field.id,
                ]);
                updateIdea(
                  { report: report._id, ...report, reportUrl: tempReportUrl },
                  ["templated", field.id],
                  true,
                );
              }}
              onError={({
                creditsafeEnabled: enabled,
                message,
              }: {
                creditsafeEnabled: boolean;
                message: string;
              }) => {
                toast.error(message);
                setCreditsafeEnabled(enabled);
              }}
            />
          </>
        ) : (
          <div style={{ marginTop: 20 }}>
            <IdeaCompanyVerification
              idea={{ _id: ideaId } as IdeaWithOwnerChallenge}
              report={reportValue as IdeaCompanyVerificationReport}
              updateReportUrl={updateReportUrl}
              field={field}
            />
            <Button
              size="small"
              style={{ marginTop: 15 }}
              color="orange"
              onClick={() => updateIdea(null, ["templated", field.id])}
              variant="outline"
            >
              <Icon icon={XIcon} />
              Use a different company
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CompanyReport;
